/**
 * useSessions / useSession — local-first session reads/writes via Dexie.
 *
 * Reads: TanStack Query queryFn calls the Dexie sessions repo. The sync engine
 * is what populates the repo from the server — the UI never waits on the
 * network to read.
 *
 * Writes: local Dexie put + mutation queue enqueue. Sync engine drains the
 * queue when authed AND online.
 *
 * Anonymous users still get useSessions; their rows are stamped with their
 * anon-${uuid} userId and stay local until they sign in (then migrate.js
 * re-stamps and the push uploads).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as sessionsRepo from '../services/db/sessions';
import * as queue from '../services/db/queue';
import { useActiveUser } from './useActiveUser';
import { requestSync } from '../services/sync/engine';

const KEYS = {
  sessions: (userId) => ['sessions', userId],
  session: (userId, id) => ['sessions', userId, id],
  track: (userId, id) => ['sessions', userId, id, 'track'],
};

export function useSessions(params) {
  const queryClient = useQueryClient();
  const { userId, isAuthed, isAnonymous, isLoading: userLoading } = useActiveUser();

  const {
    data: sessions = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [...KEYS.sessions(userId), params],
    queryFn: () => sessionsRepo.listSessions(userId, params || {}),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });

  const syncMutation = useMutation({
    mutationFn: async (sessionData) => {
      const row = await sessionsRepo.createLocalSession(
        sessionData,
        { userId, track: sessionData.track || sessionData._track },
      );
      if (isAuthed) {
        const { syncStatus, lastUpdatedAt, lastSyncError, deletedAt, userId: _u, _track, track, ...payload } = row;
        await queue.enqueue({
          entity: 'session',
          op: 'create',
          recordId: row.id,
          payload: { ...payload, track: sessionData.track || sessionData._track },
        });
        requestSync({ userId, isAuthed, isAnonymous });
      }
      return row;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.sessions(userId) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ sessionId, data }) => {
      const row = await sessionsRepo.updateLocalSession(sessionId, data);
      if (isAuthed && row) {
        await queue.enqueue({
          entity: 'session',
          op: 'update',
          recordId: sessionId,
          payload: data,
        });
        requestSync({ userId, isAuthed, isAnonymous });
      }
      return row;
    },
    onSuccess: (_row, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.sessions(userId) });
      queryClient.invalidateQueries({ queryKey: KEYS.session(userId, sessionId) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (sessionId) => {
      await sessionsRepo.softDeleteSession(sessionId);
      if (isAuthed) {
        await queue.enqueue({
          entity: 'session',
          op: 'delete',
          recordId: sessionId,
          payload: {},
        });
        requestSync({ userId, isAuthed, isAnonymous });
      }
      return sessionId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.sessions(userId) });
    },
  });

  const syncSessionFn = useCallback(
    (sessionData) => syncMutation.mutate(sessionData),
    [syncMutation],
  );
  const updateSessionFn = useCallback(
    (sessionId, data) => updateMutation.mutate({ sessionId, data }),
    [updateMutation],
  );
  const deleteSessionFn = useCallback(
    (sessionId) => deleteMutation.mutate(sessionId),
    [deleteMutation],
  );

  return {
    sessions,
    isLoading: isLoading || userLoading,
    isError,
    error,
    syncSession: syncSessionFn,
    updateSession: updateSessionFn,
    deleteSession: deleteSessionFn,
    refetch,
  };
}

export function useSession(sessionId) {
  const { userId } = useActiveUser();

  const {
    data: session,
    isLoading: sessionLoading,
    isError,
    error,
  } = useQuery({
    queryKey: KEYS.session(userId, sessionId),
    queryFn: () => sessionsRepo.getSession(sessionId),
    enabled: !!sessionId && !!userId,
    staleTime: 60 * 1000,
  });

  const {
    data: track,
    isLoading: trackLoading,
  } = useQuery({
    queryKey: KEYS.track(userId, sessionId),
    queryFn: () => sessionsRepo.getTrack(sessionId),
    enabled: !!sessionId && !!userId,
    staleTime: 60 * 1000,
  });

  return {
    session: session || null,
    track: track || null,
    segments: session?.segments || [],
    stats: session?.stats || null,
    isLoading: sessionLoading || trackLoading,
    isError,
    error,
  };
}
